import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Pencil, BookOpen, Trash2, Award, DollarSign } from 'lucide-react';
import type { Course } from '../../../types/course';
import { toast } from 'react-hot-toast';

interface CourseCardProps {
  course: Course;
  onDelete?: (courseId: string) => void;
  onEdit?: (course: Course) => void;
}

export function CourseCard({ course, onDelete, onEdit }: CourseCardProps) {
  const navigate = useNavigate();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      onDelete(course.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(course);
    } else {
      navigate(`/admin/courses/${course.id}/edit`);
    }
  };

  return (
    <div className="bg-background-card rounded-lg overflow-hidden border border-gray-800 transition-all">
      <div className="aspect-video relative group">
        <img
          src={course.image_url}
          alt={course.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
          <button
            onClick={handleEdit}
            className="p-3 bg-white rounded-xl text-gray-900 hover:bg-gray-200 transition-colors flex items-center gap-2 group"
            title="Edit Course Content"
          >
            <Pencil className="h-5 w-5" />
            <span className="text-sm font-medium">Edit Content</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/courses/${course.id}/modules`, { replace: true });
            }}
            className="p-3 bg-accent-green rounded-xl text-black hover:opacity-90 transition-opacity flex items-center gap-2 group"
            title="Manage Modules"
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-sm font-medium">Manage Modules</span>
          </button>
          <button
            onClick={handleDelete}
            className="p-3 bg-red-500 rounded-xl text-white hover:bg-red-600 transition-colors flex items-center gap-2 group"
            title="Delete Course"
          >
            <Trash2 className="h-5 w-5" />
            <span className="text-sm font-medium">Delete</span>
          </button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-2">{course.title}</h3>
        <p className="text-gray-400 text-sm line-clamp-2 mb-4">{course.description}</p>
        <div className="mt-4 space-y-2">
          <div className="flex items-center text-sm text-gray-400">
            <BookOpen className="h-4 w-4 mr-2" />
            {course.provider}
          </div>
          {course.salary_range && (
            <div className="flex items-center text-sm text-gray-400">
              <DollarSign className="h-4 w-4 mr-2" />
              {course.salary_range}
            </div>
          )}
          {course.has_certification && (
            <div className="flex items-center text-sm text-emerald-400">
              <Award className="h-4 w-4 mr-2" />
              Certification Available
            </div>
          )}
        </div>
        {course.affiliate_url && (
          <a
            href={course.affiliate_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-accent-green hover:opacity-80 text-sm mb-4 block"
          >
            Affiliate Link
          </a>
        )}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-400">{course.duration}</span>
          <span className={`px-2 py-1 rounded-full text-xs ${
            course.is_published
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {course.is_published ? 'Published' : 'Draft'}
          </span>
        </div>
      </div>
    </div>
  );
}